import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import Header from '../components/header'
import {
    CtaBand,
    CtaLink,
    FaqItem,
    FaqList,
    MarketingHero,
    MarketingInner,
    MarketingIntro,
    MarketingPage,
    MarketingSection,
    SplitBlock,
    UtilityCard,
    UtilityGrid,
} from '../components/styles/Marketing.styled'

const FAQS = [
    {
        question: 'Who is Texas Energy Group?',
        answer: 'Texas Energy Group is a Texas-based family of energy companies headquartered in Spicewood, Texas. The company helps customers reduce energy spend, save time, enhance efficiency, and minimize risk through energy procurement, risk management, utility services, infrastructure support, and market guidance.',
    },
    {
        question: 'What does Texas Energy Group do for residential electricity customers?',
        answer: 'Texas Energy Group helps residential customers navigate the Texas electricity market with confidence. Instead of sorting through confusing offers alone, customers can get guidance on plan options, contract terms, usage considerations, and enrollment details so they can make a more informed decision. Texas Energy Group brings an advanced AI advisor-style approach to electricity shopping by helping customers understand plan details, compare options, and make confident energy decisions.',
    },
    {
        question: 'Can I choose my electricity provider in Texas?',
        answer: 'In many parts of Texas, residential customers can choose a retail electricity provider while the local utility continues to deliver power, maintain poles and wires, and respond to outages. Availability depends on your service address and utility territory. Simply enter your address and let us take it from there.',
    },
    {
        question: 'What information do I need to get residential electricity rates?',
        answer: 'Typically all we need is your address.',
    },
    {
        question: 'What is an Electricity Facts Label (EFL)?',
        answer: 'An Electricity Facts Label, often called an EFL, is the disclosure document for a Texas electricity plan. It shows important details such as average prices at common usage levels, energy charges, delivery charges, contract length, renewable content, early termination fees, and other terms that affect your total bill.',
    },
    {
        question: 'Why does the advertised rate not always match my bill?',
        answer: 'Advertised prices are often based on specific usage levels, commonly 500, 1,000, or 2,000 kWh. Your actual cost can change if your usage is above or below those levels, or if the plan includes bill credits, minimum usage fees, base charges, or pass-through utility delivery charges. This is why customers like our enrollment process. We provide simple, no-hidden-fee solutions to show our customers the best residential rates available.',
    },
    {
        question: 'What is the difference between fixed-rate and variable-rate electricity plans?',
        answer: 'A fixed-rate plan locks in the energy rate for the term of the contract, which can help create more predictable bills. A variable-rate plan can change from month to month and may provide flexibility, but it can also expose customers to price changes. Texas Energy Group will never offer nor encourage our customers to go with a variable-rate plan.',
    },
    {
        question: 'What are TDU or utility delivery charges?',
        answer: 'TDU delivery charges are regulated charges from the local transmission and distribution utility that delivers electricity to your home. These charges help cover poles, wires, meter reading, infrastructure, and outage response. They appear on your bill regardless of which retail electricity provider you choose.',
    },
    {
        question: 'Will switching providers interrupt my electricity service?',
        answer: 'In most standard switches, your electricity service should not be interrupted. The change is handled electronically, and the local utility continues delivering power to the home. If you are moving into a new residence, timing your requested start date correctly is important to avoid delays.',
    },
    {
        question: 'Can Texas Energy Group help if I am moving?',
        answer: 'Yes. If you are moving, Texas Energy Group can help you review available residential electricity options for the new address and coordinate the requested start date.',
    },
    {
        question: 'What should I consider before renewing my electricity contract?',
        answer: 'Before renewing, compare your renewal offer against current market options found on our website. A renewal offer may be convenient, but it is not always the most competitive option for your home.',
    },
    {
        question: 'How do I know which electricity plan is best for my home?',
        answer: 'The best plan depends on your home’s actual usage and budget preferences, contract timing, risk tolerance, and whether you value price stability or flexibility. Texas Energy Group can help compare plan details beyond the headline rate so you understand the total cost and key terms before enrolling.',
    },
    {
        question: 'What is an ESIID, and why do you ask for my address?',
        answer: 'An ESIID is the unique ID assigned to your electric meter in Texas. We use your service address to find the right ESIID so your enrollment is tied to the correct meter from the start.',
    },
    {
        question: 'Will I have to pay a deposit?',
        answer: 'It depends on the provider and the results of a standard credit check during enrollment. If a deposit is required, you will see the amount before anything is charged, and some providers offer ways to reduce or waive it.',
    },
    {
        question: 'When will my new service start?',
        answer: 'If you are switching at your current address, service typically starts on the next available date or a date you choose at checkout. If you are moving, you pick the start date for your new address during enrollment.',
    },
    {
        question: 'Will my power go out when I switch?',
        answer: 'No. The same utility continues to deliver your electricity over the same lines. Only the company that bills you for your energy changes, so there is no interruption to your service.',
    },
    {
        question: 'Who do I call to report a power outage?',
        answer: 'Outages are handled by the utility (TDU) that delivers power to your address, not by your electricity provider. Find your utility in the section below and call the number listed; these numbers stay the same for the duration of your agreement.',
    },
]

const UTILITIES = [
    {
        name: 'CenterPoint Energy',
        area: 'Houston and surrounding areas',
        phone: '800-332-7143',
    },
    {
        name: 'Oncor Electric Delivery',
        area: 'Dallas-Fort Worth, North and West Texas',
        phone: '888-313-4747',
    },
    {
        name: 'AEP Texas',
        area: 'Abilene, West Texas, Corpus Christi, Rio Grande Valley and South Texas',
        phone: '866-223-8508',
    },
    {
        name: 'Texas-New Mexico Power (TNMP)',
        area: 'Lewisville and North Texas',
        phone: '888-866-7456',
    },
    {
        name: 'Lubbock Power & Light',
        area: 'Lubbock',
        phone: '806-775-2509',
    },
]

export const Faq = () => {
    return (
        <>
            <Helmet>
                <title>FAQ - TEXAS ENERGY GROUP</title>
                <meta
                    name="description"
                    content="Clear answers to help you shop with confidence, understand your electric bill, and choose the right electricity plan."
                />
            </Helmet>
            <Header />
            <MarketingPage id="main-content">
                <MarketingHero>
                    <MarketingInner>
                        <MarketingIntro>
                            <h1>Residential electricity FAQ</h1>
                            <p>
                                Clear answers to help you shop with confidence, understand your
                                electric bill, and choose the right electricity plan.
                            </p>
                        </MarketingIntro>
                    </MarketingInner>
                </MarketingHero>

                <MarketingSection>
                    <MarketingInner>
                        <SplitBlock>
                            <h2>Frequently asked questions</h2>
                            <p>
                                Start here if you are comparing plans, switching providers, or
                                moving into a new home. Still stuck?{' '}
                                <Link to="/contact">Send us a message</Link> and we’ll help you
                                sort it out.
                            </p>
                        </SplitBlock>
                        <FaqList>
                            {FAQS.map((item, index) => (
                                <FaqItem key={item.question} open={index === 0}>
                                    <summary>{item.question}</summary>
                                    <p>{item.answer}</p>
                                </FaqItem>
                            ))}
                        </FaqList>
                    </MarketingInner>
                </MarketingSection>

                <MarketingSection $alt>
                    <MarketingInner>
                        <SplitBlock>
                            <h2>Report a power outage</h2>
                            <p>
                                Call the utility that delivers power to your address. These numbers
                                remain in effect for the duration of your agreement.
                            </p>
                        </SplitBlock>
                        <UtilityGrid>
                            {UTILITIES.map((utility) => (
                                <UtilityCard key={utility.name}>
                                    <h3>{utility.name}</h3>
                                    <p>{utility.area}</p>
                                    <a href={`tel:${utility.phone.replace(/-/g, '')}`}>
                                        {utility.phone}
                                    </a>
                                </UtilityCard>
                            ))}
                        </UtilityGrid>
                    </MarketingInner>
                </MarketingSection>

                <CtaBand>
                    <h2>Still have a question?</h2>
                    <p>
                        Compare residential electricity plans with Texas Energy Group, or reach out
                        and we’ll help you find a clearer option for your home.
                    </p>
                    <CtaLink to="/contact">Contact us</CtaLink>
                </CtaBand>
            </MarketingPage>
        </>
    )
}
